import { HttpClient } from '@angular/common/http';
import { Inject } from '@angular/core';
import 'rxjs/operators/map';

import { GalleryService } from 'app/gallery/gallery.service';
import { importImageStoreImage } from 'app/gallery/imageprovider';
import { ProductDesignerComponentConfig
       } from 'app/product-designer-component-config';
import { ProductDesignerComponentConfigParser
       } from 'app/product-designer-component-config-parser';
import { ProductDesignerConfig } from 'app/product-designer-config';
import { ProductDesignerConfigParser
       } from 'app/product-designer-config-parser';
import { CriticalError } from 'core/error/error';
import { ErrorService } from 'core/error/error.service';
import { FontStoreService } from 'core/fontstore/fontstore.service';
import { Globals } from 'core/globals';
import { ImageStoreService } from 'core/imagestore/imagestore.service';
import { LoggingService } from 'core/logging/logging.service';
import { ProductdesignerdService
       } from 'core/productdesignerd/productdesignerd.service';
import { EventTrackingConfigParser
       } from 'core/tracking/event-tracking-config-parser';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { TranslationService } from 'core/translation/translation.service';
import { Utils } from 'core/utils';
import { ProductService } from '../product/product.service';
import { ConfigParserError } from './config-parser';

export class ConfigServiceError extends CriticalError
{
  protected _name: string = 'ConfigServiceError';
}

export class ConfigService
{
  private productDesignerComponentConfig: ProductDesignerComponentConfig;

  constructor(
      @Inject(HttpClient) private http,
      @Inject(ErrorService) private errorService,
      @Inject(ImageStoreService) private imageStoreService,
      @Inject(FontStoreService) private fontStoreService,
      @Inject(LoggingService) private loggingService,
      @Inject(ProductdesignerdService) private productdesignerdService,
      @Inject(ProductService) private productService,
      @Inject(TranslationService) private translationService,
      @Inject(EventTrackingService) private eventTrackingService,
      @Inject(GalleryService) private galleryService,
  ) {}

  private removeRedundantSlashesFromURL(URL: string): string
  {
    return URL
        .replace(/([^:]\/)\/+/g, '$1')
        .replace(/\/$/g, '');
  }

  public resolveProductDesignerConfig(configString: string):
      Promise<ProductDesignerConfig>
  {
    return this.http.get(
        configString, { observe: 'response', responseType: 'json' })
        .pipe(Utils.retryPipe())
        .toPromise()
        .then(async (response) => {
          try {
            return await this.parseProductDesignerConfig(response.body);
          }
          catch (error) {
            if (!(error instanceof ConfigParserError)) {
              error = new ConfigServiceError(
                  'parsing productdesigner-config failed');
            }
            this.errorService.add(error, 'ConfigService');
            this.errorService.flush('ConfigService');
            return undefined;
          }
        })
        .catch(error => {
          if (!error.url) {
            error.url = configString;
          }
          this.errorService
            .catchHttpOrSyntaxError(error, 'productdesigner-config');
          return undefined;
        });
  }

  private async parseProductDesignerConfig(
      configJson: any): Promise<ProductDesignerConfig>
  {
    this.parseProductDesignerComponentConfig(configJson.settings);

    if (!configJson.settings.locale) {
      throw new ConfigParserError('locale is not defined');
    }
    this.translationService.setTranslationLocale(configJson.settings.locale);

    if (configJson.tracking) {
      const trackingConfigParser =
          new EventTrackingConfigParser(configJson.tracking);
      const tracking = trackingConfigParser.getConfig();
      this.eventTrackingService.initializeTracking(tracking);
    }

    let configParser = new ProductDesignerConfigParser(
        configJson.content, this.imageStoreService);
    let config = await configParser.getConfig();
    for (let productConfig of config.products) {
      this.productService.updateProductConfig(productConfig);
    }

    const importImage = async image_data => {
      try {
        const info = await this.imageStoreService.getImageInfo(image_data.url);
        await this.galleryService.addPendingImage(importImageStoreImage(info));
      }
      catch (e) {
        this.errorService.add(
          new ConfigServiceError('could not import images'),
          'ConfigService'
        );
        this.errorService.flush('ConfigService');
      }
    };

    if (config.importImages) {
      await Promise.all(config.importImages.map(importImage));
    }

    return config;
  }

  private parseProductDesignerComponentConfig(configJson: any): void
  {
    const configParser = new ProductDesignerComponentConfigParser(configJson);
    this.productDesignerComponentConfig = configParser.getConfig();
    Globals.mode = this.productDesignerComponentConfig.mode;
    const galleriesImageStoreURL =
        this.productDesignerComponentConfig.galleriesImageStoreURL;
    const galleryImageDisposition =
        this.productDesignerComponentConfig.galleryImageDisposition;
    const fontStoreURL = this.productDesignerComponentConfig.fontStoreURL;
    const loggingURL = this.productDesignerComponentConfig.loggingURL;
    const designResultsURL =
        this.productDesignerComponentConfig.designResultsURL;
    const heartbeatURL = this.productDesignerComponentConfig.heartbeatURL;

    this.fontStoreService.fontStoreDomain =
        this.removeRedundantSlashesFromURL(fontStoreURL);

    if (galleriesImageStoreURL) {
      this.imageStoreService.galleriesImageStoreDomain =
          this.removeRedundantSlashesFromURL(galleriesImageStoreURL);
    }
    if (galleryImageDisposition) {
      this.imageStoreService.galleryImageDisposition = galleryImageDisposition;
    }
    if (loggingURL) {
      this.loggingService.loggingURL =
          this.removeRedundantSlashesFromURL(loggingURL);
    }
    if (designResultsURL) {
      this.productdesignerdService.designResultsURL =
          this.removeRedundantSlashesFromURL(designResultsURL);
    }
    if (heartbeatURL) {
      this.productdesignerdService.heartbeatURL =
          this.removeRedundantSlashesFromURL(heartbeatURL);
    }
  }

  public getProductDesignerComponentConfig(): ProductDesignerComponentConfig
  {
     return this.productDesignerComponentConfig;
  }
}



// WEBPACK FOOTER //
// ./src/app/core/config/config.service.ts